class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.isTyping = false;
    this.hasScrolledOnFocus = false;
    this.typingTimeout = null;
    this.isModalOpen = false;
    
    // Store active scroll locks for cleanup
    this.activeScrollLocks = [];
    
    this.setupEventListeners();
    this.setupModalDetection();
  }

  setupModalDetection() {
    const detailsModals = document.querySelectorAll('details-modal');
    
    detailsModals.forEach(modal => {
      const details = modal.querySelector('details');
      if (details) {
        const isSearchModal = modal.querySelector('input[type="search"]') !== null;
        
        if (isSearchModal) {
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.attributeName === 'open') {
                this.isModalOpen = details.hasAttribute('open');
                // console.log('Search modal is:', this.isModalOpen ? 'OPEN' : 'CLOSED');
                
                // Reset flags when modal closes
                if (!this.isModalOpen) {
                  this.isTyping = false;
                  this.hasScrolledOnFocus = false;
                  this.clearAllScrollLocks(); // Clear all locks on close
                }
              }
            });
          });
          
          observer.observe(details, { attributes: true });
          this.isModalOpen = details.hasAttribute('open');
        }
      }
    });
    
    window.addEventListener('modal:opening', (e) => {
      if (e.detail?.modalType === 'search') {
        this.isModalOpen = true;
      }
    });
    
    window.addEventListener('modal:closing', (e) => {
      if (e.detail?.modalType === 'search') {
        this.isModalOpen = false;
        this.isTyping = false;
        this.hasScrolledOnFocus = false;
        this.clearAllScrollLocks(); // Clear all locks on close
      }
    });
  }

  clearAllScrollLocks() {
    // Remove all active scroll locks immediately
    this.activeScrollLocks.forEach(lockData => {
      if (lockData.scrollHandler) {
        window.removeEventListener('scroll', lockData.scrollHandler, { capture: true });
        document.removeEventListener('scroll', lockData.scrollHandler, { capture: true });
      }
      if (lockData.preventScroll) {
        window.removeEventListener('wheel', lockData.preventScroll, { capture: true });
        window.removeEventListener('touchmove', lockData.preventScroll, { capture: true });
      }
      if (lockData.interval) {
        clearInterval(lockData.interval);
      }
      if (lockData.timeout) {
        clearTimeout(lockData.timeout);
      }
    });
    
    this.activeScrollLocks = [];
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => {
      input.addEventListener('input', this.onInput.bind(this));
      
      input.addEventListener('focusin', () => {
        if (!this.isModalOpen) return;
        
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        this.isTyping = true;
        
        this.preventScrollDuringTransition(scrollX, scrollY, 300);
      });
      
      input.addEventListener('keydown', () => {
        if (!this.isModalOpen) return;
        
        if (this.isTyping) {
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;
          this.preventScrollDuringTransition(scrollX, scrollY, 200);
        }
      });
      
      input.addEventListener('blur', () => {
        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
          this.isTyping = false;
          this.hasScrolledOnFocus = false;
        }, 200);
      });
    });
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
    this.hasScrolledOnFocus = false;
    this.isTyping = false;
    this.clearAllScrollLocks();
  }

  onInput(event) {
    if (!this.isModalOpen) {
      const target = event.target;
      this.keepInSync(target.value, target);
      return;
    }
    
    const target = event.target;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    this.isTyping = true;
    this.preventScrollDuringTransition(scrollX, scrollY, 200);
    this.keepInSync(target.value, target);
    
    clearTimeout(this.typingTimeout);
    this.typingTimeout = setTimeout(() => {
      this.isTyping = false;
    }, 500);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen && !this.hasScrolledOnFocus) {
      this.hasScrolledOnFocus = true;
      this.isTyping = false;
      
      this.scrollIntoView({ behavior: 'smooth' });
      
      setTimeout(() => {
        if (this.isModalOpen) {
          this.isTyping = true;
          const scrollX = window.scrollX;
          const scrollY = window.scrollY;
          this.preventScrollDuringTransition(scrollX, scrollY, 200);
        }
      }, 700);
    }
  }

  keepInSync(value, target) {
    if (this.isModalOpen) {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      
      this.allSearchInputs.forEach((input) => {
        if (input !== target) {
          input.value = value;
        }
      });
      
      requestAnimationFrame(() => {
        window.scrollTo(scrollX, scrollY);
      });
    } else {
      this.allSearchInputs.forEach((input) => {
        if (input !== target) {
          input.value = value;
        }
      });
    }
  }

  preventScrollDuringTransition(scrollX, scrollY, duration = 150) {
    if (!this.isModalOpen) return;
    
    const scrollLockHandler = (e) => {
      if (!this.isModalOpen) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      window.scrollTo(scrollX, scrollY);
    };
    
    window.addEventListener('scroll', scrollLockHandler, { capture: true, passive: false });
    document.addEventListener('scroll', scrollLockHandler, { capture: true, passive: false });
    
    const preventScroll = (e) => {
      if (!this.isModalOpen) return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    
    window.addEventListener('wheel', preventScroll, { capture: true, passive: false });
    window.addEventListener('touchmove', preventScroll, { capture: true, passive: false });
    
    const maintainPosition = setInterval(() => {
      if (!this.isModalOpen) {
        clearInterval(maintainPosition);
        return;
      }
      window.scrollTo(scrollX, scrollY);
    }, 10);
    
    const timeoutId = setTimeout(() => {
      window.removeEventListener('scroll', scrollLockHandler, { capture: true });
      document.removeEventListener('scroll', scrollLockHandler, { capture: true });
      window.removeEventListener('wheel', preventScroll, { capture: true });
      window.removeEventListener('touchmove', preventScroll, { capture: true });
      clearInterval(maintainPosition);
      
      // Remove from active locks
      const index = this.activeScrollLocks.findIndex(lock => lock.timeout === timeoutId);
      if (index > -1) {
        this.activeScrollLocks.splice(index, 1);
      }
      
      if (this.isModalOpen) {
        window.scrollTo(scrollX, scrollY);
      }
    }, duration);
    
    this.activeScrollLocks.push({
      scrollHandler: scrollLockHandler,
      preventScroll: preventScroll,
      interval: maintainPosition,
      timeout: timeoutId
    });
  }
}

customElements.define('main-search', MainSearch);